import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from '../api';
import { loadYmaps, importYmapsModule } from '../utils/loadYmaps';
import AdCard from './AdCard';
import './CatalogMapView.css';

const DEFAULT_CENTER = [37.618423, 55.751244]; // Moscow [lon, lat]

// Infrastructure layers a user can toggle on top of the map (fetched from /geocoding/poi)
const INFRA_LAYERS = [
    { key: 'school', label: 'Школы', color: '#f39c12' },
    { key: 'kindergarten', label: 'Детсады', color: '#e67e22' },
    { key: 'metro', label: 'Метро', color: '#e74c3c' },
    { key: 'shop', label: 'Магазины', color: '#27ae60' },
    { key: 'hospital', label: 'Больницы', color: '#8e44ad' },
];

function formatPillPrice(price) {
    if (price == null) return '—';
    if (price >= 1_000_000) {
        const millions = price / 1_000_000;
        return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)} млн ₽`;
    }
    if (price >= 1_000) return `${Math.round(price / 1000)} тыс ₽`;
    return `${price} ₽`;
}

const CatalogMapView = ({ ads }) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const clustererRef = useRef(null);
    const pinElsRef = useRef(new Map());      // adId -> pin DOM element (for hover/select highlighting)
    const poiCollectionsRef = useRef({});     // category -> YMapCollection
    const trafficLayersRef = useRef([]);
    const moveTimerRef = useRef(null);
    const zoomControlAddedRef = useRef(false);

    const [status, setStatus] = useState('loading'); // loading | ready | error
    const [errorMsg, setErrorMsg] = useState(null);
    const [hoveredAdId, setHoveredAdId] = useState(null);
    const [selectedAdId, setSelectedAdId] = useState(null);
    const [activeLayers, setActiveLayers] = useState(() => new Set());
    const [trafficOn, setTrafficOn] = useState(false);
    const [boundsFilterOn, setBoundsFilterOn] = useState(false);
    const [visibleAdIds, setVisibleAdIds] = useState(null); // null = no bounds filter applied yet

    const listItemRefs = useRef(new Map());

    const geoAds = useMemo(() => ads.filter(a => a.latitude != null && a.longitude != null), [ads]);
    const displayedAds = boundsFilterOn && visibleAdIds
        ? geoAds.filter(a => visibleAdIds.has(a.id))
        : geoAds;

    // --- highlight sync (hover/select) without rebuilding the clusterer ---
    useEffect(() => {
        const activeId = hoveredAdId ?? selectedAdId;
        pinElsRef.current.forEach((el, id) => {
            el.classList.toggle('catalog-map-pin--active', id === activeId);
        });
    }, [hoveredAdId, selectedAdId]);

    const scrollToCard = useCallback((adId) => {
        const el = listItemRefs.current.get(adId);
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, []);

    const recalcVisibleIds = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        try {
            const bounds = map.bounds; // [[lonMin, latMin], [lonMax, latMax]] per current viewport
            if (!bounds) return;
            const [[lonMin, latMin], [lonMax, latMax]] = bounds;
            const ids = new Set(
                geoAds
                    .filter(a => a.longitude >= Math.min(lonMin, lonMax) && a.longitude <= Math.max(lonMin, lonMax)
                        && a.latitude >= Math.min(latMin, latMax) && a.latitude <= Math.max(latMin, latMax))
                    .map(a => a.id)
            );
            setVisibleAdIds(ids);
        } catch (e) {
            console.error('Failed to compute map bounds:', e);
        }
    }, [geoAds]);

    const fetchPoi = useCallback(async (category) => {
        const map = mapRef.current;
        if (!map) return;
        const [lon, lat] = map.center ?? DEFAULT_CENTER;
        try {
            const res = await api.get('/geocoding/poi', { params: { lat, lon, category } });
            return res.data ?? [];
        } catch (e) {
            console.error('POI fetch error:', e);
            return [];
        }
    }, []);

    const rebuildPoiLayer = useCallback(async (category, ymaps3) => {
        const map = mapRef.current;
        if (!map) return;

        const existing = poiCollectionsRef.current[category];
        if (existing) {
            map.removeChild(existing);
            delete poiCollectionsRef.current[category];
        }

        const pois = await fetchPoi(category);
        if (!pois.length) return;

        const meta = INFRA_LAYERS.find(l => l.key === category);
        const collection = new ymaps3.YMapCollection({});
        pois.forEach(poi => {
            const el = document.createElement('div');
            el.className = 'catalog-map-poi';
            el.style.background = meta?.color ?? '#136EF3';
            el.title = poi.name;
            collection.addChild(new ymaps3.YMapMarker({ coordinates: [poi.longitude, poi.latitude] }, el));
        });
        map.addChild(collection);
        poiCollectionsRef.current[category] = collection;
    }, [fetchPoi]);

    const toggleLayer = (key) => {
        setActiveLayers(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
                const existing = poiCollectionsRef.current[key];
                if (existing && mapRef.current) {
                    mapRef.current.removeChild(existing);
                    delete poiCollectionsRef.current[key];
                }
            } else {
                next.add(key);
                loadYmaps().then(ymaps3 => rebuildPoiLayer(key, ymaps3));
            }
            return next;
        });
    };

    const toggleTraffic = () => {
        setTrafficOn(prev => {
            const next = !prev;
            const map = mapRef.current;
            if (!map) return next;
            if (next) {
                loadYmaps().then(ymaps3 => {
                    try {
                        const traffic = new ymaps3.YMapTrafficLayer();
                        const trafficEvents = new ymaps3.YMapTrafficEventsLayer();
                        map.addChild(traffic);
                        map.addChild(trafficEvents);
                        trafficLayersRef.current = [traffic, trafficEvents];
                    } catch (e) {
                        console.error('Traffic layer unavailable (paid Yandex Maps tariff required):', e);
                    }
                });
            } else {
                trafficLayersRef.current.forEach(layer => map.removeChild(layer));
                trafficLayersRef.current = [];
            }
            return next;
        });
    };

    // --- map init ---
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const ymaps3 = await loadYmaps();
                const [{ YMapClusterer, clusterByGrid }, controlsModule] = await Promise.all([
                    importYmapsModule('@yandex/ymaps3-clusterer'),
                    importYmapsModule('@yandex/ymaps3-controls').catch(() => null),
                ]);
                if (cancelled || !containerRef.current) return;

                const { YMap, YMapDefaultSchemeLayer, YMapFeatureDataSource, YMapLayer, YMapListener } = ymaps3;

                const firstAd = geoAds[0];
                const center = firstAd ? [firstAd.longitude, firstAd.latitude] : DEFAULT_CENTER;

                const map = new YMap(containerRef.current, { location: { center, zoom: firstAd ? 11 : 5 } });
                map.addChild(new YMapDefaultSchemeLayer());
                map.addChild(new YMapFeatureDataSource({ id: 'ads-source' }));
                map.addChild(new YMapLayer({ source: 'ads-source', type: 'markers', zIndex: 1800 }));

                if (controlsModule?.YMapZoomControl && ymaps3.YMapControls) {
                    map.addChild(new ymaps3.YMapControls({ position: 'right' }).addChild(new controlsModule.YMapZoomControl({})));
                    zoomControlAddedRef.current = true;
                }

                const markerFn = (feature) => {
                    const el = document.createElement('div');
                    el.className = 'catalog-map-pin';
                    el.textContent = formatPillPrice(feature.properties.ad.price);
                    el.addEventListener('mouseenter', () => setHoveredAdId(feature.id));
                    el.addEventListener('mouseleave', () => setHoveredAdId(null));
                    el.addEventListener('click', () => { setSelectedAdId(feature.id); scrollToCard(feature.id); });
                    pinElsRef.current.set(feature.id, el);
                    return new ymaps3.YMapMarker({ coordinates: feature.geometry.coordinates, source: 'ads-source' }, el);
                };

                const clusterFn = (coordinates, features) => {
                    const el = document.createElement('div');
                    el.className = 'catalog-map-cluster';
                    el.textContent = String(features.length);
                    el.addEventListener('click', () => {
                        const zoom = map.zoom ?? 11;
                        map.update({ location: { center: coordinates, zoom: Math.min(zoom + 2, 18) } });
                    });
                    return new ymaps3.YMapMarker({ coordinates, source: 'ads-source' }, el);
                };

                const clusterer = new YMapClusterer({
                    method: clusterByGrid({ gridSize: 64 }),
                    features: geoAds.map(a => ({
                        type: 'Feature',
                        id: a.id,
                        geometry: { type: 'Point', coordinates: [a.longitude, a.latitude] },
                        properties: { ad: a },
                    })),
                    marker: markerFn,
                    cluster: clusterFn,
                });
                map.addChild(clusterer);

                map.addChild(new YMapListener({
                    layerId: 'any',
                    onUpdate: () => {
                        clearTimeout(moveTimerRef.current);
                        moveTimerRef.current = setTimeout(() => {
                            if (boundsFilterOn) recalcVisibleIds();
                            activeLayers.forEach(cat => rebuildPoiLayer(cat, ymaps3));
                        }, 600);
                    },
                }));

                mapRef.current = map;
                clustererRef.current = clusterer;
                setStatus('ready');
            } catch (e) {
                console.error('Failed to init catalog map:', e);
                if (!cancelled) { setStatus('error'); setErrorMsg(e.message); }
            }
        })();

        return () => {
            cancelled = true;
            clearTimeout(moveTimerRef.current);
        };
        // Map is built once per mount; ad list changes are intentionally not re-triggering a full rebuild here
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (status === 'error') {
        return <div className="catalog-map-view__error">Не удалось загрузить карту{errorMsg ? `: ${errorMsg}` : ''}.</div>;
    }

    return (
        <div className="catalog-map-view">
            <div className="catalog-map-view__list">
                <div className="catalog-map-view__list-toolbar">
                    <label className="catalog-map-view__check">
                        <input type="checkbox" checked={boundsFilterOn} onChange={e => { setBoundsFilterOn(e.target.checked); if (e.target.checked) recalcVisibleIds(); }} />
                        Искать в этой области
                    </label>
                </div>
                {displayedAds.length === 0 ? (
                    <div className="catalog-map-view__empty">Нет объявлений с адресом на карте</div>
                ) : (
                    <div className="catalog-map-view__cards">
                        {displayedAds.map(ad => (
                            <div
                                key={ad.id}
                                ref={el => { if (el) listItemRefs.current.set(ad.id, el); else listItemRefs.current.delete(ad.id); }}
                                className={`catalog-map-view__card${(hoveredAdId ?? selectedAdId) === ad.id ? ' catalog-map-view__card--active' : ''}`}
                                onMouseEnter={() => setHoveredAdId(ad.id)}
                                onMouseLeave={() => setHoveredAdId(null)}
                            >
                                <AdCard ad={ad} view="grid" />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="catalog-map-view__map-wrap">
                <div className="catalog-map-view__layers">
                    {INFRA_LAYERS.map(l => (
                        <button
                            key={l.key}
                            className={`catalog-map-view__layer-chip${activeLayers.has(l.key) ? ' catalog-map-view__layer-chip--active' : ''}`}
                            style={activeLayers.has(l.key) ? { background: l.color, borderColor: l.color } : undefined}
                            onClick={() => toggleLayer(l.key)}
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        className={`catalog-map-view__layer-chip${trafficOn ? ' catalog-map-view__layer-chip--active' : ''}`}
                        onClick={toggleTraffic}
                        title="Доступно на платных тарифах Яндекс.Карт"
                    >
                        Пробки
                    </button>
                </div>
                <div ref={containerRef} className="catalog-map-view__map" />
                {status === 'loading' && <div className="catalog-map-view__overlay">Загрузка карты…</div>}
            </div>
        </div>
    );
};

export default CatalogMapView;
